import { t } from "../i18n/index.ts";
import { fetchLinkPreview, uploadGalleryPhoto } from "../api.ts";
import type { QuestPageBlock } from "../../shared/quest-page-blocks.ts";
import { MAX_GALLERY_ITEMS } from "../../shared/gallery-photo.ts";
import { button, el } from "./dom.ts";
import { bindLiveTextControl } from "../input/deferred-commit.ts";

export interface PageBlockEditorHost {
  onScheduleSave(): void;
  onEditorStateChanged(): void;
  onSyncPreview(): void;
  onRenderAll(): void;
  onToast(message: string): void;
  uploadGalleryItem(blockIndex: number, itemIndex: number, input: HTMLInputElement): Promise<void>;
}

export function paletteBlockLabel(kind: QuestPageBlock["kind"]): string {
  return t(`rt.block.${kind}.palette` as "rt.block.tip.palette");
}

export function paletteBlockIcon(kind: QuestPageBlock["kind"]): string {
  const icons: Record<QuestPageBlock["kind"], string> = {
    tip: "💡",
    quote: "❝",
    link: "🔗",
    divider: "—",
    quiz: "?",
    gallery: "🖼",
  };
  return icons[kind];
}

export function blockSectionTitle(block: QuestPageBlock): string {
  return t(`rt.block.${block.kind}.title` as "rt.block.tip.title");
}

export function blockSectionSummary(block: QuestPageBlock): string | null {
  if (block.kind === "tip") return block.text.trim() || null;
  if (block.kind === "quote") return block.text.trim() || null;
  if (block.kind === "link") return block.title?.trim() || block.url.trim() || null;
  if (block.kind === "divider") return null;
  if (block.kind === "quiz") return block.question.trim() || null;
  if (block.kind === "gallery") {
    const n = block.items.filter((i) => i.image).length;
    return n ? t("rt.block.gallery.count", { count: n }) : null;
  }
  return null;
}

export function renderPageBlock(block: QuestPageBlock, opts: { interactiveQuiz?: boolean } = {}): HTMLElement | null {
  if (block.kind === "tip") {
    if (!block.text.trim()) return null;
    const box = el("aside", "qp-block qp-block-tip");
    box.append(el("span", "qp-block-tip-icon", "💡"), el("p", "", block.text.trim()));
    return box;
  }
  if (block.kind === "quote") {
    if (!block.text.trim()) return null;
    const q = el("blockquote", "qp-block qp-block-quote");
    q.append(el("p", "", block.text.trim()));
    if (block.attribution?.trim()) q.append(el("footer", "", block.attribution.trim()));
    return q;
  }
  if (block.kind === "link") {
    if (!block.url.trim()) return null;
    let domain = block.url;
    try {
      domain = new URL(block.url).hostname.replace(/^www\./, "");
    } catch {
      /* keep raw */
    }
    const card = el("a", "qp-block qp-block-link");
    card.href = block.url;
    card.target = "_blank";
    card.rel = "noopener noreferrer";
    if (block.image) {
      const img = el("img", "qp-block-link-img") as HTMLImageElement;
      img.src = block.image;
      img.alt = "";
      card.append(img);
    }
    const body = el("div", "qp-block-link-body");
    body.append(el("span", "qp-block-link-title", block.title?.trim() || block.url.trim()), el("span", "qp-block-link-domain", domain));
    card.append(body);
    return card;
  }
  if (block.kind === "divider") return el("hr", "qp-block qp-block-divider");
  if (block.kind === "quiz") {
    if (!block.question.trim()) return null;
    const wrap = el("div", "qp-block qp-block-quiz");
    wrap.append(el("p", "qp-block-quiz-q", block.question.trim()));
    if (block.hint?.trim()) wrap.append(el("p", "qp-block-quiz-hint", block.hint.trim()));
    if (opts.interactiveQuiz && block.answer.trim()) {
      const answer = el("p", "qp-block-quiz-a");
      answer.hidden = true;
      answer.textContent = block.answer.trim();
      const reveal = button(t("rt.block.quiz.reveal"), "qp-block-quiz-reveal", () => {
        answer.hidden = false;
        reveal.hidden = true;
      });
      wrap.append(reveal, answer);
    } else if (block.answer.trim()) {
      wrap.append(el("p", "qp-block-quiz-a", block.answer.trim()));
    }
    return wrap;
  }
  if (block.kind === "gallery") {
    const slides = block.items.filter((i) => i.image);
    if (!slides.length) return null;
    const carousel = el("div", "qp-block qp-block-gallery");
    const track = el("div", "qp-gallery-track");
    for (const item of slides) {
      const slide = el("figure", "qp-gallery-slide");
      const img = el("img", "") as HTMLImageElement;
      img.src = item.image!;
      img.alt = item.caption?.trim() ?? "";
      slide.append(img);
      if (item.caption?.trim()) slide.append(el("figcaption", "", item.caption.trim()));
      track.append(slide);
    }
    carousel.append(track);
    return carousel;
  }
  return null;
}

export function editPageBlock(host: PageBlockEditorHost, block: QuestPageBlock, blockIndex: number): HTMLElement {
  const wrap = el("div", "qe-panel-form");
  if (block.kind === "tip") {
    const ta = el("textarea", "rt-textarea");
    ta.value = block.text;
    ta.placeholder = t("rt.block.tip.placeholder");
    bindLiveTextControl(ta, {
      onInput: () => {
        block.text = ta.value;
      },
      onSync: () => host.onSyncPreview(),
      scheduleSave: () => host.onScheduleSave(),
    });
    wrap.append(labeled(t("rt.block.tip.label"), ta));
    return wrap;
  }
  if (block.kind === "quote") {
    const text = el("textarea", "rt-textarea");
    text.value = block.text;
    bindLiveTextControl(text, {
      onInput: () => {
        block.text = text.value;
      },
      onSync: () => host.onSyncPreview(),
      scheduleSave: () => host.onScheduleSave(),
    });
    const attr = el("input", "rt-text");
    attr.value = block.attribution ?? "";
    attr.placeholder = t("rt.block.quote.attributionPh");
    bindLiveTextControl(attr, {
      onInput: () => {
        block.attribution = attr.value.trim() || null;
      },
      onSync: () => host.onSyncPreview(),
      scheduleSave: () => host.onScheduleSave(),
    });
    wrap.append(labeled(t("rt.block.quote.label"), text), labeled(t("rt.block.quote.attribution"), attr));
    return wrap;
  }
  if (block.kind === "link") {
    const url = el("input", "rt-text");
    url.type = "url";
    url.value = block.url;
    url.placeholder = "https://";
    const title = el("input", "rt-text");
    title.value = block.title ?? "";
    title.placeholder = t("rt.block.link.titlePh");
    const apply = () => {
      block.url = url.value.trim();
      block.title = title.value.trim() || null;
    };
    bindLiveTextControl(url, {
      onInput: apply,
      onSync: () => host.onSyncPreview(),
      scheduleSave: () => host.onScheduleSave(),
    });
    bindLiveTextControl(title, {
      onInput: apply,
      onSync: () => host.onSyncPreview(),
      scheduleSave: () => host.onScheduleSave(),
    });
    url.addEventListener("blur", () => void previewLink(block, url.value.trim(), host));
    wrap.append(labeled(t("rt.block.link.url"), url), labeled(t("rt.block.link.titleField"), title));
    return wrap;
  }
  if (block.kind === "divider") {
    wrap.append(el("p", "rt-note", t("rt.block.divider.note")));
    return wrap;
  }
  if (block.kind === "quiz") {
    const q = el("textarea", "rt-textarea");
    q.value = block.question;
    const a = el("textarea", "rt-textarea");
    a.value = block.answer;
    const hint = el("input", "rt-text");
    hint.value = block.hint ?? "";
    const sync = () => {
      block.question = q.value;
      block.answer = a.value;
      block.hint = hint.value.trim() || null;
    };
    const live = {
      onInput: sync,
      onSync: () => host.onSyncPreview(),
      scheduleSave: () => host.onScheduleSave(),
    };
    bindLiveTextControl(q, live);
    bindLiveTextControl(a, live);
    bindLiveTextControl(hint, live);
    wrap.append(
      labeled(t("rt.block.quiz.question"), q),
      labeled(t("rt.block.quiz.answer"), a),
      labeled(t("rt.block.quiz.hint"), hint),
    );
    return wrap;
  }
  const list = el("div", "qp-gallery-editor-list");
  block.items.forEach((item, itemIndex) => {
    list.append(galleryItemEditor(host, block, blockIndex, item, itemIndex));
  });
  if (block.items.length < MAX_GALLERY_ITEMS) {
    wrap.append(
      button(t("rt.block.gallery.add"), "rt-choice", () => {
        block.items.push({ image: null, caption: null });
        host.onRenderAll();
        host.onEditorStateChanged();
      }),
    );
  }
  wrap.append(list);
  return wrap;
}

function galleryItemEditor(
  host: PageBlockEditorHost,
  block: Extract<QuestPageBlock, { kind: "gallery" }>,
  blockIndex: number,
  item: { image?: string | null; caption?: string | null },
  itemIndex: number,
): HTMLElement {
  const row = el("div", "qp-gallery-editor-item");
  const fileInput = el("input", "") as HTMLInputElement;
  fileInput.type = "file";
  fileInput.accept = "image/jpeg,image/webp,image/png";
  fileInput.hidden = true;
  fileInput.addEventListener("change", () => void host.uploadGalleryItem(blockIndex, itemIndex, fileInput));
  row.append(
    button(item.image ? t("cover.replace") : t("rt.ttl.upload"), "rt-choice rt-upload", () => fileInput.click()),
    fileInput,
  );
  if (item.image) {
    const img = el("img", "qp-page-photo-preview") as HTMLImageElement;
    img.src = item.image;
    row.append(img);
  }
  const cap = el("input", "rt-text");
  cap.value = item.caption ?? "";
  cap.placeholder = t("rt.page.photoCaptionPh");
  bindLiveTextControl(cap, {
    onInput: () => {
      item.caption = cap.value.trim() || null;
    },
    onSync: () => {},
    scheduleSave: () => host.onScheduleSave(),
  });
  row.append(labeled(t("rt.page.photoCaption"), cap));
  if (block.items.length > 1) {
    row.append(
      button(t("rt.block.gallery.remove"), "rt-spot-del", () => {
        block.items.splice(itemIndex, 1);
        host.onRenderAll();
        host.onEditorStateChanged();
        host.onScheduleSave();
      }),
    );
  }
  return row;
}

async function previewLink(
  block: Extract<QuestPageBlock, { kind: "link" }>,
  url: string,
  host: PageBlockEditorHost,
): Promise<void> {
  if (!url) return;
  try {
    const preview = await fetchLinkPreview(url);
    if (!block.title?.trim() && preview.title) block.title = preview.title;
    if (!block.image && preview.image) block.image = preview.image;
    host.onSyncPreview();
    host.onEditorStateChanged();
    host.onScheduleSave();
  } catch {
    /* optional */
  }
}

function labeled(label: string, control: HTMLElement): HTMLElement {
  const row = el("label", "rt-field");
  row.append(el("span", "rt-field-label", label), control);
  return row;
}

export async function uploadGalleryItemFile(
  missionId: string,
  blockSlot: number,
  itemIndex: number,
  input: HTMLInputElement,
  item: { image?: string | null },
  host: Pick<PageBlockEditorHost, "onToast" | "onSyncPreview" | "onRenderAll" | "onScheduleSave" | "onEditorStateChanged">,
): Promise<void> {
  const file = input.files?.[0];
  input.value = "";
  if (!file) return;
  try {
    item.image = await uploadGalleryPhoto(missionId, blockSlot, itemIndex, file);
    host.onRenderAll();
    host.onEditorStateChanged();
    host.onScheduleSave();
  } catch (err) {
    host.onToast(err instanceof Error ? err.message : t("quest.saveFailed"));
  }
}
